/**
 * useUserMedia -- request a local MediaStream (camera + mic) with an adaptive
 * resolution ladder (1080p60 -> 1080p30 -> 720p30, stepping down only on
 * OverconstrainedError). Pass cameraId/micId to bind to specific devices.
 *
 * Ported ~verbatim from batesky-electron cfcalls; typed for the webapp.
 */

import {useEffect, useState} from 'react'

const TIER_1080P60 = {
  width: {exact: 1920},
  height: {exact: 1080},
  frameRate: {exact: 60},
}
const TIER_1080P30 = {
  width: {exact: 1920},
  height: {exact: 1080},
  frameRate: {exact: 30},
}
const TIER_720P30 = {
  width: {ideal: 1280},
  height: {ideal: 720},
  frameRate: {ideal: 30},
}

function withDevice(constraint: MediaTrackConstraints, deviceId?: string) {
  if (!deviceId) return constraint
  return {...constraint, deviceId: {exact: deviceId}}
}

function isOverconstrained(err: unknown): boolean {
  const name = (err as {name?: string} | null)?.name
  return (
    name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError'
  )
}

async function tryTier(
  audio: boolean | MediaTrackConstraints,
  video: MediaTrackConstraints,
): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({audio, video})
}

async function acquireWithLadder({
  cameraId,
  micId,
}: {
  cameraId?: string
  micId?: string
}): Promise<MediaStream> {
  const audio: boolean | MediaTrackConstraints = micId
    ? {deviceId: {exact: micId}}
    : true

  try {
    return await tryTier(audio, withDevice(TIER_1080P60, cameraId))
  } catch (err) {
    if (!isOverconstrained(err)) throw err
  }
  try {
    return await tryTier(audio, withDevice(TIER_1080P30, cameraId))
  } catch (err) {
    if (!isOverconstrained(err)) throw err
  }
  return tryTier(audio, withDevice(TIER_720P30, cameraId))
}

export function useUserMedia({
  cameraId,
  micId,
}: {
  cameraId?: string
  micId?: string
} = {}): {
  stream: MediaStream | null
  error: Error | null
} {
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let cancelled = false
    let acquired: MediaStream | undefined

    const run = async () => {
      try {
        const s = await acquireWithLadder({cameraId, micId})
        if (cancelled) {
          s.getTracks().forEach(t => t.stop())
          return
        }
        acquired = s
        setError(null)
        setStream(s)
      } catch (e) {
        if (!cancelled) {
          setStream(null)
          setError(e as Error)
        }
      }
    }
    void run()

    return () => {
      cancelled = true
      if (acquired) acquired.getTracks().forEach(t => t.stop())
    }
  }, [cameraId, micId])

  return {stream, error}
}
